import { useEffect, useRef, useState } from "react";
import { useSort, SortSelect } from "@/lib/sort";

const M_COLS = { name: (m) => m.name, email: (m) => m.email, shops: (m) => m.shops };
const M_OPTS = [["name", "Nome"], ["email", "Email"], ["shops", "Negozi assegnati"]];
import { toast } from "sonner";
import { BookOpen, Loader2, Plus, Save, Upload, FileSpreadsheet, Package, RefreshCw } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { ConfirmDelete, Field, inp } from "@/components/admin/AdminUi";

export function AdminManagers() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/managers").then((r) => setList(r.data));
  const { rows: mRows, sort, setSort } = useSort(list, M_COLS, { key: "name" });
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/managers", f); toast.success("Manager creato"); setF({ name: "", email: "", password: "" }); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const save = async () => {
    try { await api.put(`/admin/managers/${edit.id}`, { name: edit.name, email: edit.email, password: edit.password || "" }); toast.success("Manager aggiornato"); setEdit(null); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const toggleGuide = async (m) => {
    try { await api.put(`/admin/managers/${m.id}/guide`, { enabled: !m.guide_enabled }); toast.success(m.guide_enabled ? "Guida tolta ai negozi del manager" : "Guida attivata per il manager e tutti i suoi negozi"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (m) => {
    try { await api.delete(`/admin/managers/${m.id}`); toast.success("Manager eliminato"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold uppercase">Area manager</h1>
        <p className="text-sm text-slate-500">Account di secondo livello: il manager vede solo nome, ordini e statistiche (con margine) dei negozi che gli assegni dalla scheda di ogni cliente. Con "Collega guida ai negozi" la guida interattiva compare al manager e a tutti i suoi negozi (anche quelli aggiunti dopo); dalla scheda cliente puoi attivarla o toglierla al singolo negozio.</p>
      </div>
      <form onSubmit={create} className="rounded-xl border bg-white p-5 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end" data-testid="manager-create-form">
        <Field label="Nome"><input data-testid="manager-name-input" required className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Email di accesso"><input data-testid="manager-email-input" type="email" required className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Password (min. 8)"><input data-testid="manager-password-input" required minLength={8} className={inp} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
        <button data-testid="manager-create-btn" className="press h-10 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Crea manager</button>
      </form>
      {list?.length > 1 && <SortSelect options={M_OPTS} sort={sort} setSort={setSort} testid="managers-sort" />}
      <div className="rounded-xl border bg-white divide-y" data-testid="managers-list">
        {mRows?.map((m) => (
          <div key={m.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`manager-row-${m.email}`}>
            {edit?.id === m.id ? (
              <div className="grid sm:grid-cols-3 gap-2 flex-1">
                <input data-testid="manager-edit-name" className={inp} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                <input data-testid="manager-edit-email" className={inp} value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
                <input data-testid="manager-edit-password" className={inp} placeholder="Nuova password (lascia vuoto)" value={edit.password || ""} onChange={(e) => setEdit({ ...edit, password: e.target.value })} />
              </div>
            ) : (
              <div><div className="font-semibold">{m.name}</div><div className="text-xs text-slate-500">{m.email} · {m.shops} negozi assegnati</div></div>
            )}
            <div className="flex flex-wrap gap-2">
              <button data-testid={`manager-guide-toggle-${m.email}`} onClick={() => toggleGuide(m)} title="Mostra la guida interattiva al manager e a tutti i suoi negozi"
                className={`press h-9 px-3 rounded-md text-xs font-semibold flex items-center gap-1 ${m.guide_enabled ? "bg-green-600 text-white" : "border border-brand-navy text-brand-navy"}`}>
                <BookOpen className="w-3.5 h-3.5" /> {m.guide_enabled ? "Guida attiva sui negozi" : "Collega guida ai negozi"}
              </button>
              {edit?.id === m.id
                ? <button data-testid="manager-edit-save" onClick={save} className="press h-9 px-3 rounded-md bg-brand-yellow text-brand-navy text-xs font-semibold flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Salva</button>
                : <button data-testid={`manager-edit-${m.email}`} onClick={() => setEdit({ ...m, password: "" })} className="press h-9 px-3 rounded-md border text-xs font-semibold">Modifica</button>}
              <ConfirmDelete small testId={`manager-delete-${m.email}`} title={`Eliminare ${m.name}?`} text="Il manager non potrà più accedere; i negozi restano invariati." onConfirm={() => del(m)} />
            </div>
          </div>
        ))}
        {list?.length === 0 && <p className="p-5 text-sm text-slate-500">Nessun manager. Creane uno qui sopra, poi assegnagli i negozi dalla scheda cliente.</p>}
      </div>
    </div>
  );
}

function UploadBox({ testId, icon: Icon, title, children, onFile, busy }) {
  const ref = useRef(null);
  return (
    <div className="rounded-xl border bg-white p-5 space-y-3" data-testid={testId}>
      <h3 className="text-lg font-bold uppercase flex items-center gap-2"><Icon className="w-5 h-5 text-brand-navy" /> {title}</h3>
      <div className="text-sm text-slate-600 space-y-1">{children}</div>
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" data-testid={`${testId}-file`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
      <button data-testid={`${testId}-btn`} disabled={busy} onClick={() => ref.current?.click()} className="press h-10 px-4 rounded-md bg-brand-navy text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica CSV
      </button>
    </div>
  );
}

function WooProducts() {
  const [st, setSt] = useState(null);
  const [job, setJob] = useState(null);
  const load = () => api.get("/admin/woocommerce/products-status").then((r) => { setSt(r.data); if (r.data.last?.status === "running") setJob(r.data.last); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const t = setTimeout(() => api.get(`/admin/import/jobs/${job.id}`).then((r) => { setJob(r.data); if (r.data.status !== "running") load(); }), 2500);
    return () => clearTimeout(t);
  }, [job]);
  const start = async () => {
    try { const { job_id } = (await api.post("/admin/woocommerce/sync-products")).data; setJob({ id: job_id, status: "running", stats: {} }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!st) return null;
  const j = job || st.last;
  const s = j?.stats || {};
  return (
    <div className="rounded-xl border-2 border-brand-navy bg-white p-5 space-y-3" data-testid="wc-products-box">
      <h3 className="text-lg font-bold uppercase flex items-center gap-2"><RefreshCw className="w-5 h-5 text-brand-navy" /> Prodotti e foto da halfatimbri.it</h3>
      <p className="text-sm text-slate-600">Scarica tutti i prodotti con <b>tutte le foto</b>, codice, descrizione, misura e categoria (gommini, cuscinetti, quadrati…), poi associa in automatico cuscinetti e gommini della stessa misura. I prodotti già presenti ricevono solo foto e dati mancanti: prezzi, regole e descrizioni modificate da te restano invariati. Prodotti con foto: <b data-testid="wc-products-photo-count">{st.with_photos}</b>.</p>
      <button data-testid="wc-products-sync-btn" disabled={j?.status === "running"} onClick={start} className="press h-10 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
        {j?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Importa prodotti e foto
      </button>
      {j && (
        <div className="text-sm" data-testid="wc-products-result">
          <div className="font-semibold">{j.status === "running" ? `Importazione in corso… ${s.processed || 0} di ${j.total || "?"} (le foto richiedono qualche minuto)` : j.status === "error" ? `Errore: ${j.error}` : `Ultima importazione: ${fmtDate(j.finished_at || j.created_at)}`}</div>
          {j.status !== "error" && <div>Nuovi <b>{s.created || 0}</b> · aggiornati {s.updated || 0} · foto {s.images || 0} · da controllare {s.to_review || 0}{s.linked != null && ` · cuscinetti associati ${s.linked} · gommini ${s.gommini}`}{s.errors ? ` · errori ${s.errors}` : ""}</div>}
        </div>
      )}
    </div>
  );
}

function WooSync() {
  const [st, setSt] = useState(null);
  const [job, setJob] = useState(null);
  const load = () => api.get("/admin/woocommerce/status").then((r) => { setSt(r.data); if (r.data.last?.status === "running") setJob(r.data.last); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const t = setTimeout(() => api.get(`/admin/import/jobs/${job.id}`).then((r) => { setJob(r.data); if (r.data.status !== "running") load(); }), 2000);
    return () => clearTimeout(t);
  }, [job]);
  const start = async () => {
    try { const { job_id } = (await api.post("/admin/woocommerce/sync-orders")).data; setJob({ id: job_id, status: "running", stats: {} }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!st) return null;
  const j = job || st.last;
  const s = j?.stats || {};
  return (
    <div className="rounded-xl border-2 border-brand-navy bg-white p-5 space-y-3" data-testid="wc-sync-box">
      <h3 className="text-lg font-bold uppercase flex items-center gap-2"><RefreshCw className="w-5 h-5 text-brand-navy" /> Collegamento diretto halfatimbri.it</h3>
      {!st.configured ? <p className="text-sm text-slate-600">Collegamento non configurato.</p> : (
        <>
          <p className="text-sm text-slate-600">Scarica tutti gli ordini dal negozio WooCommerce <b>{st.store}</b> (con anteprima del timbro, opzioni scelte, dati di fatturazione) e li collega agli account con la stessa email. Puoi ripeterlo quando vuoi: aggiorna gli ordini modificati e non crea doppioni. Ordini storici presenti: <b data-testid="wc-imported-count">{st.imported_orders}</b>.</p>
          <button data-testid="wc-sync-btn" disabled={j?.status === "running"} onClick={start} className="press h-10 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
            {j?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizza ordini ora
          </button>
          {j && (
            <div className="text-sm" data-testid="wc-sync-result">
              <div className="font-semibold">{j.status === "running" ? `Sincronizzazione in corso… ${s.processed || 0} di ${j.total || "?"}` : j.status === "error" ? `Errore: ${j.error}` : `Ultima sincronizzazione: ${fmtDate(j.finished_at || j.created_at)}`}</div>
              {j.status !== "error" && <div>Nuovi <b>{s.created || 0}</b> · aggiornati {s.updated || 0} · invariati {s.unchanged || 0} · collegati a un account <b>{s.linked || 0}</b> · email senza account {s.unlinked_emails || 0}{s.errors ? ` · errori ${s.errors}` : ""}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AdminImport() {
  const [job, setJob] = useState(null);
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState("");
  const [publish, setPublish] = useState(true);
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const t = setTimeout(() => api.get(`/admin/import/jobs/${job.id}`).then((r) => setJob(r.data)), 1500);
    return () => clearTimeout(t);
  }, [job]);
  const send = async (url, file, params) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post(url, fd, { params })).data;
  };
  const products = async (file) => {
    setBusy("p");
    try { const { job_id } = await send("/admin/import/products", file, { publish }); setJob({ id: job_id, status: "running", stats: {} }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const ordersUp = async (file) => {
    setBusy("o");
    try { const d = await send("/admin/import/orders", file); setOrders(d); toast.success(`${d.created} ordini storici importati`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const st = job?.stats || {};
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold uppercase">Importa da WooCommerce</h1>
        <p className="text-sm text-slate-500">Porta su Halfa Stamps prodotti e storico ordini di halfatimbri.it.</p>
      </div>
      <WooProducts />
      <WooSync />
      <div className="grid lg:grid-cols-2 gap-5">
        <UploadBox testId="import-products" icon={Package} title="Prodotti" onFile={products} busy={busy === "p" || job?.status === "running"}>
          <p>WordPress → Prodotti → <b>Esporta</b> → tutte le colonne → CSV.</p>
          <p>Creo una scheda per ogni prodotto con foto, prezzo, descrizione e misura della placca letta da titolo o descrizione (es. "14x38", "Ø 40 mm"). I prodotti senza misura o prezzo restano <b>nascosti</b> e segnati "da controllare".</p>
          <label className="flex items-center gap-2 pt-1"><input type="checkbox" data-testid="import-products-publish" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="w-4 h-4 accent-brand-navy" /> Pubblica subito i prodotti completi</label>
        </UploadBox>
        <UploadBox testId="import-orders" icon={FileSpreadsheet} title="Storico ordini" onFile={ordersUp} busy={busy === "o"}>
          <p>Esporta gli ordini da WooCommerce in CSV (es. plugin "Advanced Order Export" o "WooCommerce → Ordini → Esporta") con numero, data, stato, email e dati di fatturazione, prodotti, quantità e totali.</p>
          <p>Gli ordini vengono collegati ai clienti tramite email, numerati <span className="font-mono">HT-numero</span> e mostrati nello storico del cliente, nell'admin e nelle statistiche manager. Reimportare lo stesso file non crea doppioni.</p>
        </UploadBox>
      </div>
      {job && (
        <div className="rounded-xl border-2 border-brand-navy bg-white p-5 text-sm space-y-2" data-testid="import-products-result">
          <div className="font-semibold flex items-center gap-2">{job.status === "running" && <Loader2 className="w-4 h-4 animate-spin" />}
            {job.status === "running" ? `Importazione prodotti in corso… ${st.processed || 0}${job.total ? ` di ${job.total}` : ""}` : job.status === "error" ? `Errore: ${job.error}` : "Importazione prodotti completata"}</div>
          {job.status !== "error" && <div>Creati <b data-testid="import-products-created">{st.created || 0}</b> · già presenti {st.skipped_existing || 0} · foto scaricate {st.images || 0} · da controllare <b>{st.to_review || 0}</b> · righe non valide {st.skipped_invalid || 0}</div>}
          {job.status === "done" && st.to_review > 0 && <div className="text-xs text-slate-500">Trovi i prodotti da controllare in "Prodotti ed editor": completa misura o prezzo e attivali.</div>}
        </div>
      )}
      {orders && (
        <div className="rounded-xl border-2 border-brand-navy bg-white p-5 text-sm" data-testid="import-orders-result">
          Ordini nel file <b>{orders.orders_in_file}</b> · importati <b data-testid="import-orders-created">{orders.created}</b> · già presenti {orders.skipped_existing} · collegati a clienti {orders.linked_customers} · articoli riconosciuti {orders.matched_products} · senza data valida {orders.invalid}
        </div>
      )}
    </div>
  );
}
